import React from 'react';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { useHeroData } from './hooks';

import DiagonalArrow from '../assets/diagonalArrow.inline.svg';
import DiagonalArrowDesktop from '../assets/diagonalArrowDesktop.inline.svg';
import RightArrow from '../assets/rightArrow.inline.svg';
import RightArrowDesktop from '../assets/rightArrowDesktop.inline.svg';

import { filterProps } from 'utils/helpers';
import { GLOBAL_STYLE_VARIABLES } from 'theme';
import { LAYOUT_VARIABLES } from 'components/Layout';
import { RichText } from 'components/Prismic';

interface IHeroSection {
  id: string;
}

export enum HERO_SECTION_VARIABLES {
  titleColor = '--title-color',
  strongColor = '--strong-color',
  textColor = '--text-color',
  highlightsColor = '--highlights-color',
  strokeColor = '--stroke-color',
  titleTextScale = '--title-text-scale',
  titleFontSize = '--title-font-size',
}

const Container = styled('div', filterProps('isDark'))<{
  isDark: boolean;
}>`
  ${({ theme: { colors, mq }, isDark }) => css`
    ${HERO_SECTION_VARIABLES.titleColor}: ${isDark ? '#DFDFDF' : `${colors.textDark}E5`};
    ${HERO_SECTION_VARIABLES.strongColor}: ${isDark ? '#DFDFDF' : colors.primary};
    ${HERO_SECTION_VARIABLES.textColor}: ${isDark ? '#B5B5B5CC' : `${colors.textDark}E5`};
    ${HERO_SECTION_VARIABLES.highlightsColor}: ${isDark ? '#B5B5B5CC' : `${colors.textDark}E5`};
    ${HERO_SECTION_VARIABLES.strokeColor}: ${isDark ? colors.primary : colors.black};
    ${HERO_SECTION_VARIABLES.titleTextScale}: 1;
    ${HERO_SECTION_VARIABLES.titleFontSize}: 24.5rem;

    background-color: ${isDark ? colors.backgroundDark : colors.offWhite};

    ${mq.md} {
      ${HERO_SECTION_VARIABLES.titleFontSize}: 8.375rem;
    }
  `}
`;

const FullScreenWrapper = styled.div`
  ${({ theme: { mq, spacings } }) => css`
    box-sizing: border-box;
    display: grid;
    row-gap: 5.5rem;
    align-content: center;
    padding: calc(var(${LAYOUT_VARIABLES.headerHeight}) - 2rem) 5.25rem 3rem;
    min-height: calc((1rem * var(${GLOBAL_STYLE_VARIABLES.fullScreenRatio})) + 1px);

    ${mq.md} {
      row-gap: 1.75rem;
      padding: calc(var(${LAYOUT_VARIABLES.headerHeight}) + 1.625rem) ${spacings.padding} 4rem;
      min-height: initial;
    }
  `}
`;

const TitleContainer = styled.div`
  ${({ theme: { mq } }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 2rem 0;

    ${mq.md} {
      overflow: hidden;
    }
  `}
`;

const TitleSizer = styled.div`
  display: flex;
  height: calc(
    var(${HERO_SECTION_VARIABLES.titleFontSize}) * var(${HERO_SECTION_VARIABLES.titleTextScale}) *
      0.82
  );
`;

const Title = styled(RichText)`
  ${({ theme: { tp } }) => css`
    display: flex;
    flex-direction: column;

    & > h1 {
      transform: scale(var(${HERO_SECTION_VARIABLES.titleTextScale}));
      transform-origin: left;
      line-height: calc(0.82 * var(${HERO_SECTION_VARIABLES.titleTextScale}));
      letter-spacing: -0.09em;
      color: var(${HERO_SECTION_VARIABLES.titleColor});
      font-size: var(${HERO_SECTION_VARIABLES.titleFontSize});

      ${tp.useFont(fonts => fonts.Nimbus.regular)};

      & > strong {
        color: var(${HERO_SECTION_VARIABLES.strongColor});

        ${tp.useFont(fonts => fonts.Nimbus.regular)};
      }
    }
  `}
`;

const DescriptionContainer = styled.div`
  ${({ theme: { mq } }) => css`
    display: grid;
    grid-template-columns: min-content auto;
    column-gap: 5.5rem;

    ${mq.md} {
      column-gap: 2rem;
    }
  `}
`;

const Description = styled(RichText)`
  ${({ theme: { tp, mq } }) => css`
    display: flex;
    flex-direction: column;

    & > h3 {
      ${tp.useFont(fonts => fonts.Switzer.light)};

      line-height: 1.3;
      letter-spacing: -0.02em;
      color: var(${HERO_SECTION_VARIABLES.textColor});
      font-size: 2.625rem;
    }

    ${mq.md} {
      & > h3 {
        line-height: 1.1;
        font-size: 1.75rem;

        & > br {
          display: none;
        }
      }
    }
  `}
`;

const StyledDiagonalMobileArrow = styled(DiagonalArrow)`
  ${({ theme: { mq } }) => css`
    width: 2.25rem;
    height: 2.25rem;

    & path {
      stroke: var(${HERO_SECTION_VARIABLES.strokeColor});
    }

    ${mq.mdMin} {
      display: none;
    }
  `}
`;

const StyledDiagonalDesktopArrow = styled(DiagonalArrowDesktop)`
  ${({ theme: { mq } }) => css`
    width: 4.25rem;
    height: 4.25rem;

    & path {
      stroke: var(${HERO_SECTION_VARIABLES.strokeColor});
    }

    ${mq.md} {
      display: none;
    }
  `}
`;

const Highlights = styled.div`
  ${({ theme: { mq, spacings } }) => css`
    display: grid;
    grid-template-rows: repeat(4, auto);
    grid-auto-columns: 1fr;
    grid-auto-flow: column;
    row-gap: 1.5rem;
    padding: 3.5rem 12rem 4.75rem;

    ${mq.md} {
      grid-template-rows: auto;
      grid-auto-flow: row;
      row-gap: 2rem;
      padding: 0 ${spacings.padding} 5rem;
    }
  `}
`;

const Highlight = styled.span`
  ${({ theme: { tp, mq } }) => css`
    ${tp.useFont(fonts => fonts.Switzer.lightest)};

    display: grid;
    grid-template-columns: min-content auto;
    column-gap: 2.25rem;
    align-items: center;
    line-height: 1.2;
    letter-spacing: -0.02em;
    color: var(${HERO_SECTION_VARIABLES.highlightsColor});
    font-size: 3rem;
    font-weight: 250;

    & path {
      stroke: var(${HERO_SECTION_VARIABLES.strokeColor});
    }

    ${mq.md} {
      column-gap: 2rem;
      line-height: 1.3;
      font-size: 1.75rem;
    }
  `}
`;

const HighlightIconDesktop = styled(RightArrowDesktop)`
  ${({ theme: { mq } }) => css`
    width: 4rem;
    height: 4rem;

    ${mq.md} {
      display: none;
    }
  `}
`;

const HighlightIconMobile = styled(RightArrow)`
  ${({ theme: { mq } }) => css`
    width: 2.25rem;
    height: 2.25rem;

    ${mq.mdMin} {
      display: none;
    }
  `}
`;

const HeroSection: React.FC<IHeroSection> = ({ id }) => {
  const { isDark, title, description, highlights, titleContainerRef, getTitleRef } = useHeroData();

  return (
    <Container id={id} isDark={isDark}>
      <FullScreenWrapper>
        <TitleContainer ref={titleContainerRef}>
          {title.map(titleText => {
            const key = JSON.stringify(titleText);

            return (
              <TitleSizer key={key} ref={getTitleRef(key)}>
                <Title render={titleText} />
              </TitleSizer>
            );
          })}
        </TitleContainer>
        <DescriptionContainer>
          <StyledDiagonalMobileArrow />
          <StyledDiagonalDesktopArrow />
          <Description render={description} />
        </DescriptionContainer>
      </FullScreenWrapper>
      <Highlights>
        {highlights.map((highlight, index) => (
          <Highlight key={JSON.stringify(`${highlight}${index}`)}>
            <HighlightIconMobile />
            <HighlightIconDesktop />
            {highlight}
          </Highlight>
        ))}
      </Highlights>
    </Container>
  );
};

export const heroSectionFragment = graphql`
  fragment SolutionHeroData on PrismicSolution {
    data {
      is_dark
      title {
        richText
      }
      description {
        richText
      }
      highlights {
        highlights_text
      }
    }
  }
`;

export default HeroSection;
